import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { styled } from '@mui/system';
import moment from 'moment';
// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: '#f9f9f9',
  borderRadius: 8,
  boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
  border: '1px solid #e0e0e0',
}));

const Label = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  fontSize: '0.95rem',
}));

const Value = styled(Typography)(({ theme, hasValue }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
  fontSize: '0.95rem',
  wordBreak: 'break-word',
  backgroundColor: hasValue ? '#e8f5e9' : 'transparent', // Green highlight for entered values
  padding: hasValue ? theme.spacing(0.5) : 0,
  borderRadius: hasValue ? 4 : 0,
}));

const FieldContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const InfoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(0.5),
}));

const formatDate = (dateString) => {
  if (!dateString) return '';
  // const date = new Date(dateString);
  // const month = String(date.getMonth() + 1).padStart(2, '0');
  // const day = String(date.getDate()).padStart(2, '0');
  // const year = date.getFullYear();
  return moment(dateString).format('MM/DD/YYYY');
};

const formatValue = (value) => (value ? value : '');

const SummaryTab = ({ bondDetails, canViewPostingAgency, canViewRequestingAgency }) => {
  return (
    <Box sx={{ mt: 4 }}>
      <StyledPaper>
        {/* Header */}
        <InfoContainer>
          <InfoIcon color="primary" sx={{ mr: 0.5, fontSize: '1rem' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
            Transfer Bond Details
          </Typography>
        </InfoContainer>
        <Divider sx={{ mb: 1 }} />

        {/* Content */}
        <Grid container spacing={0.5}>
          {/* Reference # */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Reference #:</Label>
              <Value hasValue={!!bondDetails?.transferBondTransactionID}>
                {formatValue(bondDetails?.transferBondTransactionID)}
              </Value>
            </FieldContainer>
          </Grid>
          {/* Total Bond Amount */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Total Bond Amount:</Label>
              <Value hasValue={bondDetails?.totalBondAmount !== undefined}>
                $
                {bondDetails?.totalBondAmount !== undefined
                  ? bondDetails.totalBondAmount.toLocaleString()
                  : '-'}
              </Value>
            </FieldContainer>
          </Grid>

          {/* Number of Bonds */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Number of Bonds:</Label>
              <Value hasValue={!!bondDetails?.numberOfBonds}> {formatValue(bondDetails?.numberOfBonds)}</Value>
            </FieldContainer>
          </Grid>

          {/* Created At */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Created At:</Label>
              <Value hasValue={!!bondDetails?.createdAt}>{formatDate(bondDetails?.createdAt?.substring(0, 10))}</Value>
            </FieldContainer>
          </Grid>

          {/* Execution Fee */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Execution Fee:</Label>
              <Value hasValue={!!bondDetails?.executionFee}>
                $
                {bondDetails?.executionFee !== undefined
                  ? bondDetails.executionFee.toLocaleString()
                  : '-'}
              </Value>
            </FieldContainer>
          </Grid>

          {/* Travel Fee */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Travel Fee:</Label>
              <Value hasValue={!!bondDetails?.travelFee}>
                $
                {bondDetails?.travelFee !== undefined
                  ? bondDetails.travelFee.toLocaleString()
                  : '-'}
              </Value>
            </FieldContainer>
          </Grid>

          {/* Other Fees */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Other Fees:</Label>
              <Value hasValue={!!bondDetails?.otherFees}>
                $
                {bondDetails?.otherFees !== undefined
                  ? bondDetails.otherFees.toLocaleString()
                  : '-'}
              </Value>
            </FieldContainer>
          </Grid>

          {/* Defendant Name */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Defendant Name:</Label>
              <Value hasValue={!!(bondDetails?.defendantFirstName || bondDetails?.defendantMiddleInitial || bondDetails?.defendantLastName)}>
                {`${formatValue(bondDetails?.defendantFirstName)} ${formatValue(
                  bondDetails?.defendantMiddleInitial
                )} ${formatValue(bondDetails?.defendantLastName)}`}
              </Value>
            </FieldContainer>
          </Grid>

          {/* Defendant Date of Birth */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Defendant DOB:</Label>
              <Value hasValue={!!bondDetails?.defendantDateOfBirth}>{formatDate(bondDetails?.defendantDateOfBirth?.substring(0, 10))}</Value>
            </FieldContainer>
          </Grid>

          {/* Place of Birth */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Place of Birth:</Label>
              <Value hasValue={!!bondDetails?.placeOfBirth}>{formatValue(bondDetails?.placeOfBirth)}</Value>
            </FieldContainer>
          </Grid>

          {/* Defendant SSN */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Social Security Number:</Label>
              <Value hasValue={!!bondDetails?.defendantSsn}>{formatValue(bondDetails?.defendantSsn)}</Value>
            </FieldContainer>
          </Grid>

          {/* Defendant Phone Number */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Defendant Phone:</Label>
              <Value hasValue={!!bondDetails?.defendantPhone}>{formatValue(bondDetails?.defendantPhone)}</Value>
            </FieldContainer>
          </Grid>

          {/* Defendant DL Number */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Defendant DL #:</Label>
              <Value hasValue={!!bondDetails?.defendantDriversLicenseNumber}>{formatValue(bondDetails?.defendantDriversLicenseNumber)}</Value>
            </FieldContainer>
          </Grid>

          {/* Driver's License State */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Driver's License State:</Label>
              <Value hasValue={!!bondDetails?.driversLicenseState}>{formatValue(bondDetails?.driversLicenseState)}</Value>
            </FieldContainer>
          </Grid>

          {/* Defendant Race */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Defendant Race:</Label>
              <Value hasValue={!!bondDetails?.defendantRace}>{formatValue(bondDetails?.defendantRace)}</Value>
            </FieldContainer>
          </Grid>

          {/* Defendant Gender */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Defendant Gender:</Label>
              <Value hasValue={!!bondDetails?.defendantGender}>{formatValue(bondDetails?.defendantGender)}</Value>
            </FieldContainer>
          </Grid>

          {/* Booking Number */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Booking Number:</Label>
              <Value hasValue={!!bondDetails?.bookingNumber} >{formatValue(bondDetails?.bookingNumber)}</Value>
            </FieldContainer>
          </Grid>

          {/* Arrest Date */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Arrest Date:</Label>
              <Value hasValue={!!bondDetails?.arrestDate}>{formatDate(bondDetails?.arrestDate?.substring(0, 10))}</Value>
            </FieldContainer>
          </Grid>

          {/* Court Code */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Court Code:</Label>
              <Value hasValue={!!bondDetails?.courtCode}>{formatValue(bondDetails?.courtCode)}</Value>
            </FieldContainer>
          </Grid>

          {/*Court */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Court:</Label>
              <Value hasValue={!!bondDetails?.courtPlace}>{formatValue(bondDetails?.courtPlace)}</Value>
            </FieldContainer>
          </Grid>

          {/* Jail */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Jail:</Label>
              <Value hasValue={!!bondDetails?.jailLocation}>{formatValue(bondDetails?.jailLocation)}</Value>
            </FieldContainer>
          </Grid>

          {/* Jail Phone */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Jail Phone:</Label>
              <Value hasValue={!!bondDetails?.jailPhone}>{formatValue(bondDetails?.jailPhone)}</Value>
            </FieldContainer>
          </Grid>

          {/* Jail Info */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Jail Info:</Label>
              <Value hasValue={!!bondDetails?.jailInfo}>{formatValue(bondDetails?.jailInfo)}</Value>
            </FieldContainer>
          </Grid>

          {/* Defendant Address */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Defendant Address:</Label>
              <Value hasValue={!!(bondDetails?.defendantStreet || bondDetails?.defendantCity || bondDetails?.defendantState || bondDetails?.defendantZipCode)}>
              {bondDetails?.defendantStreet || bondDetails?.defendantCity || bondDetails?.defendantState || bondDetails?.defendantZipCode
                ? `${formatValue(bondDetails?.defendantStreet)}, ${formatValue(
                    bondDetails?.defendantCity
                  )}, ${formatValue(bondDetails?.defendantState)}, ${formatValue(
                    bondDetails?.defendantZipCode
                  )}`
                    .split(', ')
                    .filter((part) => part.trim() !== '') // Remove empty parts
                    .join(', ') // Join with commas only if there are valid parts
                : '-'}
            </Value>
            </FieldContainer>
          </Grid>

          {/* County */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>County:</Label>
              <Value hasValue={!!bondDetails?.county}>{formatValue(bondDetails?.county)}</Value>
            </FieldContainer>
          </Grid>

          {/* State */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>State:</Label>
              <Value hasValue={!!bondDetails?.state}>{formatValue(bondDetails?.state)}</Value>
            </FieldContainer>
          </Grid>

          {/* XREF Number */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>XREF Number:</Label>
              <Value hasValue={!!bondDetails?.xrefNumber}>{formatValue(bondDetails?.xrefNumber)}</Value>
            </FieldContainer>
          </Grid>

          {/* PFN Number */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>PFN Number:</Label>
              <Value hasValue={!!bondDetails?.pfnNumber}>{formatValue(bondDetails?.pfnNumber)}</Value>
            </FieldContainer>
          </Grid>

          {/* Hair Color */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Hair Color:</Label>
              <Value hasValue={!!bondDetails?.hairColor}>{formatValue(bondDetails?.hairColor)}</Value>
            </FieldContainer>
          </Grid>

          {/* Eye Color */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Eye Color:</Label>
              <Value hasValue={!!bondDetails?.eyeColor}>{formatValue(bondDetails?.eyeColor)}</Value>
            </FieldContainer>
          </Grid>

          {/* Weight */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Weight:</Label>
              <Value hasValue={!!bondDetails?.weight}>{formatValue(bondDetails?.weight)}</Value>
            </FieldContainer>
          </Grid>

          {/* Height */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Height:</Label>
              <Value hasValue={!!bondDetails?.height}>{formatValue(bondDetails?.height)}</Value>
            </FieldContainer>
          </Grid>

          {/* Nearest Relative Name */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Nearest Relative Name:</Label>
              <Value hasValue={!!bondDetails?.nearestRelativeName}>{formatValue(bondDetails?.nearestRelativeName)}</Value>
            </FieldContainer>
          </Grid>

          {/* Nearest Relative Address */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Nearest Relative Address:</Label>
              <Value hasValue={!!(bondDetails?.nearestRelativeAddress)}>
                {bondDetails?.nearestRelativeAddress ? (
                  `${formatValue(bondDetails.nearestRelativeAddress.street1)}, ${formatValue(
                    bondDetails.nearestRelativeAddress.city
                  )}, ${formatValue(bondDetails.nearestRelativeAddress.state)}, ${formatValue(
                    bondDetails.nearestRelativeAddress.zipCode
                  )}`
                ) : (
                  '-'
                )}
              </Value>
            </FieldContainer>
          </Grid>

          {/* Next of Kin Name */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Next of Kin Name:</Label>
              <Value hasValue={!!bondDetails?.nextOfKinName}>{formatValue(bondDetails?.nextOfKinName)}</Value>
            </FieldContainer>
          </Grid>

          {/* Next of Kin Phone Number */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Next of Kin Phone #:</Label>
              <Value hasValue={!!bondDetails?.nextOfKinPhoneNumber}>{formatValue(bondDetails?.nextOfKinPhoneNumber)}</Value>
            </FieldContainer>
          </Grid>

          {/* Next of Kin Address */}
          <Grid item xs={12} sm={6} md={3}>
            <FieldContainer>
              <Label>Next of Kin Address:</Label>
              <Value hasValue={!!(bondDetails?.nextOfKinAddress)}>
                {bondDetails?.nextOfKinAddress ? (
                  `${formatValue(bondDetails.nextOfKinAddress.street1)}, ${formatValue(
                    bondDetails.nextOfKinAddress.city
                  )}, ${formatValue(bondDetails.nextOfKinAddress.state)}, ${formatValue(
                    bondDetails.nextOfKinAddress.zipCode
                  )}`
                ) : (
                  '-'
                )}
              </Value>
            </FieldContainer>
          </Grid>
          {/* Posting Agency */}
          {canViewPostingAgency && (
            <Grid item xs={12} sm={6} md={3}>
              <FieldContainer>
                <Label>Posting Agency:</Label>
                <Value hasValue={!!bondDetails?.postingAgencyName}>
                  {bondDetails?.postingAgencyName || '-'}
                </Value>
              </FieldContainer>
            </Grid>
          )}
          {/* Requesting Agency (Shown Conditionally) */}
          {canViewRequestingAgency && (
            <Grid item xs={12} sm={6} md={3}>
              <FieldContainer>
                <Label>Requesting Agency:</Label>
                <Value hasValue={!!bondDetails?.requestingAgencyName}>
                  {bondDetails?.requestingAgencyName || '-'}
                </Value>
              </FieldContainer>
            </Grid>
          )}
        </Grid>
      </StyledPaper>
    </Box>
  );
};

export default SummaryTab;
